import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { CpfCnpjPipe } from '../../../../../../../shared/pipes/cpf-cnpj.pipe';
import { CampoBuscaClienteEnum } from '../../../../../../../shared/enums/pessoa/campo-busca-cliente.enum';
import { PessoaLookupService } from '../../../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ClienteVendaSelectStruct } from '../../../../../../../shared/services/structs/pessoa/cliente-venda-select.struct';

/** Uma opção do menu "refinar busca". */
interface OpcaoRefino {
    campo: CampoBuscaClienteEnum;
    label: string;
}

/**
 * Modal "Pesquisar cadastro" (§5.1) — a caçada, quando o autocomplete do campo não basta.
 *
 * Diferente da busca do campo em três coisas:
 *   REFINO ..... a pesquisa pode ser por um campo só (nome inteiro, fantasia, e-mail, documento,
 *                telefone), e não na varredura geral
 *   SOB DEMANDA. só pesquisa quando o usuário manda, pela lupa ou pelo Enter — aqui ele está
 *                montando um critério, e buscar a cada tecla atrapalharia
 *   PAGINADA ... 5 por vez, com a contagem do total
 *
 * Sem termo a busca traz todos, que é o que o texto de orientação promete.
 */
@Component({
    selector: 'app-vd-cliente-busca-dialog',
    standalone: true,
    imports: [
        CpfCnpjPipe,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './vd-cliente-busca-dialog.component.html',
    styleUrl: './vd-cliente-busca-dialog.component.css',
})
export class VdClienteBuscaDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<VdClienteBuscaDialogComponent, ClienteVendaSelectStruct>);
    private readonly pessoaLookupService = inject(PessoaLookupService);

    public readonly opcoesRefino: OpcaoRefino[] = [
        { campo: CampoBuscaClienteEnum.NomeParcial, label: 'Nome (parcial)' },
        { campo: CampoBuscaClienteEnum.Nome, label: 'Nome' },
        { campo: CampoBuscaClienteEnum.Fantasia, label: 'Fantasia' },
        { campo: CampoBuscaClienteEnum.Email, label: 'E-mail' },
        { campo: CampoBuscaClienteEnum.CpfCnpj, label: 'CPF/CNPJ' },
        { campo: CampoBuscaClienteEnum.Telefone, label: 'Telefone' },
    ];

    public termo: string = '';
    public refino: OpcaoRefino | null = null;
    public resultados: ClienteVendaSelectStruct[] = [];
    public total: number = 0;
    public isBuscando: boolean = false;
    public jaPesquisou: boolean = false;

    /** Quantos já foram carregados. O modal cresce de 5 em 5. */
    private readonly tamanhoPagina = 5;

    public get subtitulo(): string {
        return this.refino
            ? `Refinando por ${this.refino.label.toLowerCase()}`
            : 'Busca em todos os campos do cadastro';
    }

    public get placeholder(): string {
        return this.refino
            ? `Pesquise por ${this.refino.label.toLowerCase()}`
            : 'Pesquise por nome, fantasia, e-mail, CPF/CNPJ ou telefone';
    }

    public get contagem(): string {
        const encontrados = this.total === 1 ? '1 cadastro encontrado' : `${this.total} cadastros encontrados`;

        return this.resultados.length < this.total
            ? `${encontrados} — mostrando ${this.resultados.length}`
            : encontrados;
    }

    public get temMais(): boolean {
        return this.resultados.length < this.total;
    }

    public escolherRefino(opcao: OpcaoRefino | null): void {
        this.refino = opcao;

        // Refino trocado depois de uma busca refaz a pesquisa: o critério mudou, e deixar na tela
        // o resultado do critério anterior é mostrar resposta de outra pergunta.
        if (this.jaPesquisou) this.pesquisar();
    }

    public aoTeclar(evento: KeyboardEvent): void {
        if (evento.key === 'Enter') this.pesquisar();
    }

    public pesquisar(): void {
        this.jaPesquisou = true;
        this.buscar(0);
    }

    public verMais(): void {
        this.buscar(this.resultados.length / this.tamanhoPagina);
    }

    private buscar(pagina: number): void {
        this.isBuscando = true;

        const campo = this.refino?.campo ?? CampoBuscaClienteEnum.Todos;

        this.pessoaLookupService.pesquisarClienteVenda(this.termo, campo, pagina, this.tamanhoPagina).subscribe({
            next: (res) => {
                this.isBuscando = false;

                if (res.isError) {
                    this.resultados = [];
                    this.total = 0;
                    return;
                }

                this.resultados = pagina === 0 ? res.list : [...this.resultados, ...res.list];
                this.total = res.totalRegistros;
            },
            error: () => {
                this.isBuscando = false;
                this.resultados = [];
                this.total = 0;
            },
        });
    }

    public escolher(item: ClienteVendaSelectStruct): void {
        this.dialogRef.close(item);
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    public inicialDe(item: ClienteVendaSelectStruct): string {
        return (item.nome ?? '?').trim().charAt(0).toUpperCase();
    }
}
